import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useNavigate, useOutletContext } from "react-router-dom";
const Account = () => {
  const navigate = useNavigate();
  const {
    user,
    profile
  } = useOutletContext<{
    user: any;
    profile: any;
  }>();

  // Password change state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  const [editingPassword, setEditingPassword] = useState(false);

  // Email change state
  const [newEmail, setNewEmail] = useState("");
  const [changingEmail, setChangingEmail] = useState(false);
  const [editingEmail, setEditingEmail] = useState(false);

  // Username change state
  const [newUsername, setNewUsername] = useState("");
  const [changingUsername, setChangingUsername] = useState(false);
  const [editingUsername, setEditingUsername] = useState(false);

  // Display Name change state
  const [newDisplayName, setNewDisplayName] = useState("");
  const [changingDisplayName, setChangingDisplayName] = useState(false);
  const [editingDisplayName, setEditingDisplayName] = useState(false);

  // Delete account state
  const [deletingAccount, setDeletingAccount] = useState(false);
  const getInitials = (name: string) => {
    if (!name) return "?";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };
  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("New passwords don't match");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setChangingPassword(true);
    try {
      const {
        error
      } = await supabase.auth.updateUser({
        password: newPassword
      });
      if (error) throw error;
      toast.success("Password changed successfully");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setEditingPassword(false);
    } catch (error: any) {
      toast.error(error.message || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };
  const handleUsernameChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUsername || newUsername.trim().length === 0) {
      toast.error("Please enter a valid username");
      return;
    }

    // Basic username validation
    const usernameRegex = /^[a-zA-Z0-9_-]+$/;
    if (!usernameRegex.test(newUsername.trim())) {
      toast.error("Username can only contain letters, numbers, underscores, and hyphens");
      return;
    }
    if (newUsername.trim().length < 3) {
      toast.error("Username must be at least 3 characters");
      return;
    }
    setChangingUsername(true);
    try {
      const {
        error
      } = await supabase.from("profiles").update({
        username: newUsername.trim()
      }).eq("id", user.id);
      if (error) {
        if (error.code === '23505') {
          toast.error("Username already taken");
        } else {
          throw error;
        }
        return;
      }
      toast.success("Username updated successfully. Your profile URL has changed.");
      setNewUsername("");
      setEditingUsername(false);
    } catch (error: any) {
      toast.error(error.message || "Failed to update username");
    } finally {
      setChangingUsername(false);
    }
  };
  const handleDisplayNameChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setChangingDisplayName(true);
    try {
      const {
        error
      } = await supabase.from("profiles").update({
        full_name: newDisplayName.trim() || null
      }).eq("id", user.id);
      if (error) throw error;
      toast.success("Display name updated successfully");
      setNewDisplayName("");
      setEditingDisplayName(false);
    } catch (error: any) {
      toast.error(error.message || "Failed to update display name");
    } finally {
      setChangingDisplayName(false);
    }
  };
  const handleEmailChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail || !newEmail.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    setChangingEmail(true);
    try {
      const {
        error
      } = await supabase.auth.updateUser({
        email: newEmail
      });
      if (error) throw error;
      toast.success("Confirmation email sent. Please check your inbox.");
      setNewEmail("");
    } catch (error: any) {
      toast.error(error.message || "Failed to change email");
    } finally {
      setChangingEmail(false);
    }
  };
  const handleDeleteAccount = async () => {
    setDeletingAccount(true);
    try {
      const {
        error
      } = await supabase.functions.invoke("delete-account");
      if (error) throw error;
      toast.success("Account deleted successfully");
      await supabase.auth.signOut();
      navigate("/");
    } catch (error: any) {
      toast.error(error.message || "Failed to delete account");
      setDeletingAccount(false);
    }
  };
  return <div className="space-y-6 lg:ml-12">
      {/* Profile Header */}
      <div className="flex items-center gap-3 md:gap-4 pb-4 md:pb-6">
        <Avatar className="h-16 w-16 md:h-20 md:w-20">
          <AvatarImage src={profile?.avatar_url} />
          <AvatarFallback className="bg-gradient-to-br from-primary to-primary/60 text-primary-foreground text-lg md:text-xl font-bold">
            {getInitials(profile?.full_name || profile?.username || user?.email)}
          </AvatarFallback>
        </Avatar>
        <div>
          <h1 className="text-xl md:text-2xl font-bold">
            {profile?.full_name || profile?.username || "User"}
          </h1>
          <p className="text-xs md:text-sm text-muted-foreground break-all">{user?.email}</p>
        </div>
      </div>

      {/* Personal Details */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Personal details</h2>
        
        <div className="space-y-0">
          {/* Display Name */}
          <div className="flex items-start justify-between py-4">
            {!editingDisplayName ? <>
                <div className="flex-1">
                  <h3 className="font-medium text-base mb-1">Display Name</h3>
                  <p className="text-sm text-muted-foreground">
                    {profile?.full_name || "Not set"}
                  </p>
                  
                </div>
                <Button variant="ghost" size="sm" onClick={() => setEditingDisplayName(true)} className="text-foreground hover:underline hover:bg-transparent hover:text-foreground focus:text-foreground active:text-foreground">
                  Edit
                </Button>
              </> : <div className="space-y-4 w-full">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-semibold">Edit display name</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      This is how your name appears to other users.
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => {
                setEditingDisplayName(false);
                setNewDisplayName("");
              }} className="text-foreground hover:underline hover:bg-transparent hover:text-foreground focus:text-foreground active:text-foreground">
                    Cancel
                  </Button>
                </div>
                <form onSubmit={handleDisplayNameChange} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="newDisplayName" className="text-base font-medium">Display Name</Label>
                    <Input id="newDisplayName" type="text" value={newDisplayName} onChange={e => setNewDisplayName(e.target.value)} placeholder={profile?.full_name || "Enter your display name"} className="bg-muted" />
                  </div>
                  <Button type="submit" size="lg" disabled={changingDisplayName} className="rounded-full px-8">
                    {changingDisplayName ? "Saving..." : "Save"}
                  </Button>
                </form>
              </div>}
          </div>

          {/* Username */}
          <div className="border-t flex items-start justify-between py-4">
            {!editingUsername ? <>
                <div className="flex-1">
                  <h3 className="font-medium text-base mb-1">Username</h3>
                  <p className="text-sm text-muted-foreground">
                    {profile?.username || "Not set"}
                  </p>
                  
                </div>
                <Button variant="ghost" size="sm" onClick={() => setEditingUsername(true)} className="text-foreground hover:underline hover:bg-transparent hover:text-foreground focus:text-foreground active:text-foreground">
                  Edit
                </Button>
              </> : <div className="space-y-4 w-full">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-semibold">Edit username</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      This is your username on MacroVault. Your profile URL will be /profile/{newUsername || profile?.username}.
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => {
                setEditingUsername(false);
                setNewUsername("");
              }} className="text-foreground hover:underline hover:bg-transparent hover:text-foreground focus:text-foreground active:text-foreground">
                    Cancel
                  </Button>
                </div>
                <form onSubmit={handleUsernameChange} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="newUsername" className="text-base font-medium">Username</Label>
                    <Input id="newUsername" type="text" value={newUsername} onChange={e => setNewUsername(e.target.value)} placeholder={profile?.username || "Enter your username"} className="bg-muted" />
                    <p className="text-xs text-muted-foreground">
                      Usernames are auto-created when you sign up. You can customize yours here.
                    </p>
                  </div>
                  <Button type="submit" size="lg" disabled={changingUsername} className="rounded-full px-8">
                    {changingUsername ? "Saving..." : "Save"}
                  </Button>
                </form>
              </div>}
          </div>

          {/* Email */}
          <div className="border-t py-4">
            {!editingEmail ? <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-medium text-base mb-1">Email address</h3>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setEditingEmail(true)} className="text-foreground hover:underline hover:bg-transparent hover:text-foreground focus:text-foreground active:text-foreground">
                  Edit
                </Button>
              </div> : <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-semibold">Edit email address</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      This will be used for logging in and account recovery.
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => {
                setEditingEmail(false);
                setNewEmail("");
              }} className="text-foreground hover:underline hover:bg-transparent hover:text-foreground focus:text-foreground active:text-foreground">
                    Cancel
                  </Button>
                </div>
                <form onSubmit={handleEmailChange} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="newEmail" className="text-base font-medium">Email address</Label>
                    <Input id="newEmail" type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder={user?.email} className="bg-muted" />
                  </div>
                  <Button type="submit" size="lg" disabled={changingEmail} className="rounded-full px-8">
                    {changingEmail ? "Saving..." : "Save"}
                  </Button>
                </form>
              </div>}
          </div>

          {/* Password */}
          <div className="border-t py-4">
            {!editingPassword ? <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-medium text-base mb-1">Password</h3>
                  <p className="text-sm text-muted-foreground">••••••••</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setEditingPassword(true)} className="text-foreground hover:underline hover:bg-transparent hover:text-foreground focus:text-foreground active:text-foreground">
                  Change
                </Button>
              </div> : <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-semibold">Edit password</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Password needs to be 8 characters and contain at least one alphabet and one number.
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => {
                setEditingPassword(false);
                setNewPassword("");
                setConfirmPassword("");
              }} className="text-foreground hover:underline hover:bg-transparent hover:text-foreground focus:text-foreground active:text-foreground">
                    Cancel
                  </Button>
                </div>
                <form onSubmit={handlePasswordChange} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="newPassword" className="text-base font-medium">New password</Label>
                      <Input id="newPassword" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Enter password" className="bg-muted" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword" className="text-base font-medium">Repeat password</Label>
                      <Input id="confirmPassword" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Repeat password" className="bg-muted" />
                    </div>
                  </div>
                  <Button type="submit" size="lg" disabled={changingPassword} className="rounded-full px-6 md:px-8">
                    {changingPassword ? "Saving..." : "Save"}
                  </Button>
                </form>
              </div>}
          </div>
        </div>
      </div>


      {/* Manage Account */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Manage account</h2>
        
        <div className="flex items-start justify-between py-4">
          <div className="flex-1">
            <h3 className="font-medium text-base mb-1">Delete account</h3>
            <p className="text-sm text-muted-foreground">Permanently delete your MacroVault account.</p>
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" className="text-destructive hover:text-destructive hover:bg-transparent" disabled={deletingAccount}>
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                <AlertDialogDescription>
                  This action cannot be undone. This will permanently delete your
                  account and remove all your data from our servers including:
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>Your profile and personal information</li>
                    <li>All your published macros</li>
                    <li>Your reviews and comments</li>
                    <li>All other account data</li>
                  </ul>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDeleteAccount} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                  Delete Account
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>;
};
export default Account;